/**
 * Phát âm thanh MÁY CHỦ gửi kèm dòng SSE — và nghe câu hỏi bằng giọng nói (port `hooks/giong.ts` native).
 *
 * Bỏ `speechSynthesis` của trình duyệt: danh sách giọng là của máy người dùng và mỗi máy một khác (Chrome/Linux không có
 * giọng tiếng Việt nào; Firefox/Linux đẻ ra ~30 biến thể đọc y hệt nhau). Máy chủ chỉ có MỘT bộ giọng.
 *
 * Âm thanh đi chung dòng SSE với chữ: máy chủ cắt tới ranh giới CÂU, tổng hợp ngay trong lúc còn đang viết. Việc của
 * tệp này chỉ còn là XẾP HÀNG. Danh sách giọng: `useDanhSachGiong` ở `useChung.ts`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

// ── Hàng đợi phát ────────────────────────────────────────────────────────

/**
 * Khoảng nghỉ CHÈN THÊM giữa hai mẩu (giây).
 *
 * ── ĐO TRƯỚC KHI CHỌN SỐ ───────────────────────────────────────────────────
 * Bản trước để 0.14 với lý do "nối sát trơn thì câu sau đạp lên câu trước".
 * Đo lại trên bốn mẩu thật (22050 Hz · mono · 16-bit):
 *
 *     mẩu   dài(s)   lặng đầu   lặng cuối
 *       1    11.05        0 ms       40 ms
 *       2    13.35        0 ms       39 ms
 *       3     7.74        0 ms       40 ms
 *       4     5.73        0 ms       40 ms
 *
 * Máy chủ ĐÃ để sẵn ~40 ms nghỉ ở cuối mỗi mẩu — đó chính là quãng ngắt cuối
 * câu do bộ tổng hợp sinh ra. Cộng thêm 0.14 nữa thành ~180 ms im ở mỗi chỗ
 * nối, dài bằng một âm tiết, và tai nghe ra đúng là "giật cục".
 *
 * Nỗi lo "đạp lên nhau" không xảy ra được: `batDau` luôn là
 * `Math.max(currentTime + DEM_DAU, moc)`, mà `moc` = điểm kết thúc mẩu trước
 * cộng đúng hằng này. Đặt 0 thì hai mẩu KỀ SÁT, không bao giờ chồng. Lời cảnh
 * báo ấy có lẽ có từ trước khi có cơ chế `moc`.
 *
 * Giữ 0.02 chứ không về 0: đủ nuốt sai số làm tròn khi trình duyệt lấy mẫu lại
 * 22050 → 48000 Hz, và vẫn dưới ngưỡng nghe thấy. Cùng giá trị SDK nhúng đang
 * dùng — hai nơi phát cùng một dòng byte thì phải nghe giống nhau.
 *
 * Điểm mấu chốt của bản trước vẫn đúng: tai nghe ra sự KHÔNG ĐỀU chứ không
 * nghe ra độ dài. Chỗ sai là con số, không phải lý lẽ.
 */
const NGHI_GIUA_MAU = 0.02;

/**
 * Đệm trước khi phát mẩu đầu. `decodeAudioData` mất vài chục mili giây; đặt
 * lịch vào đúng `currentTime` thì mẩu đầu bị cắt mất phần đầu.
 */
const DEM_DAU = 0.08;

/**
 * Số mẩu phải có TRONG TAY trước khi phát mẩu đầu.
 *
 * ── VÌ SAO CẦN CHỜ, DÙ MÁY CHỦ SINH TIẾNG RẤT NHANH ───────────────────────
 * Ràng buộc không nằm ở tổng hợp mà ở TRUYỀN. Đo trên một mẩu thật: 191 ký tự
 * → WAV 462 KB → base64 616 KB → 10,7 giây tiếng, tức ~57 KB base64 cho mỗi
 * giây tiếng, và mẩu chạm trần `TRAN_KY_TU` lên tới ~1,3 MB. Muốn nuôi kịp tai
 * thì đường truyền phải giữ đều ≥57 KB/s, qua thêm một lớp proxy gateway.
 *
 * Chữ thì bé và tới tức thì; tiếng thì không. Nên có ca: mẩu đầu phát xong,
 * dòng SSE đã tới, mà mẩu tiếng thứ hai vẫn đang trên đường — và tai nghe ra
 * một khoảng ngắt giữa câu trả lời.
 *
 * Trước bản này KHÔNG có gì hấp thụ trễ ấy: `moc` có tạo khoảng dẫn trước,
 * nhưng nó bắt đầu từ 0, nên chỗ nối ĐẦU TIÊN luôn trần trụi.
 *
 * ── VÌ SAO CHỜ Ở ĐÂY RẺ ───────────────────────────────────────────────────
 * `main.py` tổng hợp tiếng SAU khi lời văn đã viết xong và qua kiểm số. Nghĩa
 * là chờ mẩu thứ hai KHÔNG phải chờ mô hình viết — chỉ chờ truyền thêm một
 * mẩu. Nếu tiếng được sinh trong lúc mô hình còn viết thì phép chờ này sẽ đắt;
 * ở đây thì không.
 *
 * 2 mẩu: mẩu đầu thường 3–13 giây tiếng, nên chỉ cần có mẩu thứ hai là khoảng
 * dẫn trước đã rộng hơn mọi dao động truyền hợp lý.
 */
// Giữ lại để ghim lịch sử đo — thước đo đang dùng là `NGUONG_GIAY_CHO` bên dưới.
export const NGUONG_MAU_CHO = 2;

/**
 * Ngưỡng mồi tính bằng GIÂY TIẾNG đang có trong tay, không bằng SỐ MẨU.
 *
 * ── VÌ SAO PHẢI ĐỔI THƯỚC ĐO ──────────────────────────────────────────────
 * `NGUONG_MAU_CHO = 2` đúng khi một "mẩu" là trọn một câu — 3 đến 13 giây
 * tiếng, nên hai mẩu là một khoảng dẫn trước rộng hơn mọi dao động truyền hợp
 * lý.
 *
 * Với dòng PCM thì một mẩu chỉ còn ~0,4 giây. Đếm mẩu ở đây là phát ngay sau
 * 0,8 giây tiếng, tức mồi gần như không có, và chỗ nối đầu tiên lại trần trụi
 * đúng như trước khi có phép mồi.
 *
 * Đo thời lượng thì MỘT luật đúng cho cả hai đường: hai mẩu WAV vượt ngưỡng
 * này từ lâu, còn dòng PCM gom đủ rồi mới chạy.
 */
const NGUONG_GIAY_CHO = 1.2;

/**
 * Trần thời gian chờ. Đường truyền xấu thì KHÔNG được chờ vô hạn — thà nghe
 * sớm và có thể ngắt, còn hơn im lặng mãi không hiểu vì sao.
 *
 * Cùng với tín hiệu `done` bên dưới, đây là hai đường thoát khiến câu trả lời
 * chỉ có MỘT mẩu vẫn phát được. Thiếu chúng thì `NGUONG_MAU_CHO` sẽ chờ một
 * mẩu thứ hai không bao giờ tới — đó là cách hỏng kinh điển của mọi hàng đợi
 * đệm, và nó im lặng tuyệt đối.
 */
const TRAN_CHO_MS = 3_000;

/**
 * PCM16 thô → `AudioBuffer`, KHÔNG qua `decodeAudioData`.
 *
 * `decodeAudioData` cần một tệp có phần đầu (WAV, MP3…); PCM trần thì không có
 * gì để nó đọc. Nhưng ta biết chính xác định dạng — máy chủ khai `tanSo`, và
 * Gemini luôn trả 16-bit một kênh — nên dựng thẳng còn ĐÚNG HƠN: không đoán
 * định dạng, không lấy mẫu lại, và **đồng bộ** thay vì trả về một Promise.
 *
 * Đồng bộ là điều đáng kể ở đây: đường WAV phải xếp hàng `decodeAudioData` qua
 * một chuỗi Promise để hai mẩu tới sát nhau không giải mã xong ngược thứ tự.
 * Dựng thẳng thì thứ tự là thứ tự gọi, không cần chuỗi ấy.
 */
/**
 * PCM16 thô → `AudioBuffer` ở ĐÚNG tần số của `AudioContext`.
 *
 * ── VÌ SAO TỰ LẤY MẪU LẠI THAY VÌ ĐỂ TRÌNH DUYỆT LÀM ─────────────────────
 * Bản trước tạo buffer ở 24 kHz (`createBuffer(1, n, 24000)`) rồi để trình
 * duyệt lo. `AudioContext` thường chạy 48 kHz, nên nó lấy mẫu lại — và nó làm
 * việc ấy cho TỪNG buffer MỘT CÁCH ĐỘC LẬP, mỗi buffer một trạng thái bộ lọc
 * mới. Mỗi ranh giới buffer thành một chỗ đứt.
 *
 * Đường WAV cũ cũng đi qua phép lấy mẫu lại ấy, nhưng nó chỉ có MỘT buffer cho
 * trọn một câu — bốn câu là bốn ranh giới, không ai nghe ra. Đường phát theo
 * dòng cắt cùng lượng âm thanh ấy thành ~0,4 giây một mẩu: đo trên bản chạy là
 * **79 mẩu cho 4 câu**, tức 79 chỗ đứt, và tai nghe ra thành tiếng "tút tút".
 *
 * Nội suy tuyến tính là đủ cho tiếng nói và KHÔNG có trạng thái bộ lọc, nên
 * ghép hai mẩu liền nhau không sinh ra chỗ đứt nào. `truoc` mang mẫu CUỐI của
 * mẩu liền trước sang làm mốc nội suy — thiếu nó thì mẫu đầu mỗi mẩu nội suy
 * từ số 0, và đó lại đúng là một chỗ đứt nữa.
 */
/** Số mẫu VÀO giữ lại làm lịch sử cho nhân Lanczos của mẩu kế tiếp. */
const DUOI_LICH = 8;

/** Nửa bề rộng nhân Lanczos. 3 là chỗ cân giữa chất lượng và 6 phép nhân mỗi
 * mẫu ra — đủ rẻ để chạy đồng bộ ngay trong `them()`. */
const LANCZOS_A = 3;

/** Nhân Lanczos-3, có hệ số cắt để dùng được cả khi HẠ tần số lấy mẫu. */
function nhanLanczos(x: number, cut: number): number {
  const y = x * cut;
  if (y === 0) return cut;
  if (Math.abs(y) >= LANCZOS_A) return 0;
  const p = Math.PI * y;
  return cut * ((Math.sin(p) / p) * (Math.sin(p / LANCZOS_A) / (p / LANCZOS_A)));
}

/** `DUOI_LICH` mẫu cuối của dòng vào, ghép từ mẩu này và lịch sử cũ nếu mẩu
 * ngắn hơn `DUOI_LICH`. */
function duoiMau(f: Float32Array, lich: Float32Array): Float32Array {
  const moi = new Float32Array(DUOI_LICH);
  for (let i = 0; i < DUOI_LICH; i++) {
    const k = f.length - DUOI_LICH + i;
    moi[i] = k >= 0 ? (f[k] ?? 0) : (lich[DUOI_LICH + k] ?? 0);
  }
  return moi;
}

function tuPcm16(
  c: AudioContext,
  byte: Uint8Array,
  tanSo: number,
  lich: Float32Array,
  viTri = 0,
): { buf: AudioBuffer | null; lich: Float32Array; viTri: number } {
  const mau = new Int16Array(byte.buffer, byte.byteOffset, byte.byteLength >> 1);
  if (!mau.length) return { buf: null, lich, viTri };
  // 32768 chứ không 32767: `Int16` chạy từ -32768 tới 32767, chia cho 32768 thì
  // mọi giá trị nằm trong [-1, 1) và không mẫu nào bị kẹp méo.
  const f = new Float32Array(mau.length);
  for (let i = 0; i < mau.length; i++) f[i] = (mau[i] ?? 0) / 32768;

  const ti = c.sampleRate / tanSo;
  if (Math.abs(ti - 1) < 1e-6) {
    const buf = c.createBuffer(1, f.length, c.sampleRate);
    buf.getChannelData(0).set(f);
    return { buf, lich: duoiMau(f, lich), viTri: 0 };
  }

  // ── VÌ SAO KHÔNG CÒN LÀ NỘI SUY TUYẾN TÍNH ───────────────────────────────
  // Nội suy tuyến tính là một bộ lọc thông thấp rất tù: đáp ứng của nó là
  // sinc², và nó ăn mất cao tần theo tần số. Đo 24 kHz → 48 kHz, biên độ giữ
  // lại:
  //
  //     tần số     tuyến tính   Lanczos-3
  //      2 kHz     -0,15 dB      -0,02 dB
  //      4 kHz     -0,58 dB      -0,02 dB
  //      6 kHz     -1,25 dB      +0,07 dB
  //      8 kHz     -2,04 dB      -0,42 dB
  //
  // Dải 4–8 kHz là chỗ nằm của phụ âm xát (`s`, `x`, `ch`) — thứ quyết định
  // giọng nghe TRONG hay ĐỤC, và cũng là thứ tai dùng để phân biệt giọng nữ.
  // Mất 1–2 dB ở đó nghe ra là "giọng bị đục", và vì nó chỉ xảy ra trên đường
  // PHÁT THEO DÒNG (mẩu ~0,4 giây, hàng chục mẩu một bài) nên nó trông như
  // lỗi của model chứ không như lỗi của máy nghe.
  //
  // ── PHA VẪN PHẢI ĐI TIẾP QUA CHỖ NỐI ─────────────────────────────────────
  // `viTri` mang vị trí đọc còn dở sang mẩu sau, và `lich` mang `DUOI_LICH`
  // mẫu cuối để nhân Lanczos còn đủ cánh trái. Thiếu một trong hai thì mỗi mẩu
  // tự đặt lại pha hoặc nội suy từ số 0 — và mỗi chỗ nối thành một tiếng xước.
  //
  // Nhân cần 3 mẫu VÀO phía sau, nên mỗi mẩu chừa lại tối đa 3 mẫu (0,125 ms)
  // cho mẩu kế tiếp. Ở mẩu CUỐI của bài, 3 mẫu ấy không bao giờ được phát —
  // ngắn hơn một phần tám mili giây, không ai nghe ra.
  const buoc = tanSo / c.sampleRate;
  const cut = Math.min(1, c.sampleRate / tanSo);
  const L = f.length;
  const lay = (k: number): number => (k < 0 ? (lich[DUOI_LICH + k] ?? 0) : (f[k] ?? 0));

  let pos = viTri;
  let n = 0;
  for (let p = pos; Math.floor(p) + LANCZOS_A <= L - 1; p += buoc) n++;
  // Mẩu ngắn hơn bề rộng nhân thì KHÔNG có mẫu ra nào — vẫn phải mang pha và
  // lịch sử sang mẩu sau, nếu không chỗ nối ấy lại là chỗ đặt lại pha.
  if (n < 1) return { buf: null, lich: duoiMau(f, lich), viTri: pos - L };

  const buf = c.createBuffer(1, n, c.sampleRate);
  const ra = buf.getChannelData(0);
  for (let i = 0; i < n; i++) {
    const base = Math.floor(pos);
    let s = 0;
    for (let k = base - LANCZOS_A + 1; k <= base + LANCZOS_A; k++) {
      s += lay(k) * nhanLanczos(pos - k, cut);
    }
    ra[i] = s;
    pos += buoc;
  }
  return { buf, lich: duoiMau(f, lich), viTri: pos - L };
}

function giaiB64(b64: string): Uint8Array {
  const bin = atob(b64);
  const u = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u[i] = bin.charCodeAt(i);
  return u;
}

/**
 * Nhận mẩu tiếng theo dòng SSE, phát NỐI LIỀN.
 *
 * ── VÌ SAO KHÔNG DÙNG `<audio>` ───────────────────────────────────────────
 * `onended` là một sự kiện của vòng lặp sự kiện, không có cách nào bảo trình
 * duyệt "bắt đầu mẩu này đúng lúc mẩu kia dứt" — mỗi chỗ nối mất ~100ms nạp
 * blob và giải mã. Web Audio giải mã sẵn thành `AudioBuffer` rồi `start(khiNao)`
 * đặt lịch trên đồng hồ card âm thanh, chính xác tới từng mẫu và không phụ
 * thuộc vòng lặp sự kiện đang bận vẽ lại React hay không.
 */
export function useDocTo() {
  const [dangDoc, setDangDoc] = useState(false);
  // ── TẠM DỪNG LÀ MỘT TRẠNG THÁI RIÊNG, KHÔNG PHẢI "KHÔNG ĐANG ĐỌC" ───────
  // `dangDoc` vẫn TRUE trong lúc tạm dừng: bài đọc chưa xong, chỉ là đồng hồ
  // đứng lại. Gộp hai thứ vào một cờ thì nút loa mất chỗ để nói "Tiếp tục", và
  // người dùng chỉ còn đường bấm tắt rồi bật lại — tức đọc lại từ đầu.
  const [dangTam, setDangTam] = useState(false);
  // Bản `ref` của `dangTam`, cho `may()` đọc. `may()` là `useCallback` rỗng phụ
  // thuộc nên nó nhìn thấy state của lần dựng ĐẦU — đọc `dangTam` ở đó là đọc
  // một giá trị vĩnh viễn `false`.
  const tamRef = useRef(false);
  const ctx = useRef<AudioContext | null>(null);
  // Mốc thời gian mẩu KẾ TIẾP phải bắt đầu, trên đồng hồ của `AudioContext`.
  // Đây là toàn bộ cơ chế nối liền.
  const moc = useRef(0);
  const dangPhat = useRef<AudioBufferSourceNode[]>([]);
  // Mẩu đã giải mã nhưng CHƯA đặt lịch — hàng chờ mồi. Rỗng sau khi đã phát.
  // Kèm chỉ số CÂU: khoảng nghỉ chỉ được chèn giữa hai CÂU, không giữa hai mẩu
  // của cùng một câu — chèn vào giữa câu là cắt ngang một từ.
  const cho = useRef<{ buf: AudioBuffer; cau: number }[]>([]);
  // Câu của mẩu vừa đặt lịch, để biết chỗ nối nào là ranh giới câu.
  const cauTruoc = useRef<number | null>(null);
  // `DUOI_LICH` mẫu VÀO cuối cùng của mẩu vừa dựng — cánh trái của nhân
  // Lanczos ở mẩu kế tiếp. Xem `tuPcm16`.
  const lichMau = useRef<Float32Array>(new Float32Array(DUOI_LICH));
  // Vị trí đọc còn dở, tính theo mẫu VÀO, mang sang mẩu sau. Thiếu nó thì mỗi
  // mẩu đặt lại pha và chỗ nối kêu — xem `tuPcm16`.
  const viTri = useRef(0);
  // Đã qua giai đoạn mồi chưa. Qua rồi thì mẩu tới là đặt lịch ngay.
  const daChay = useRef(false);
  const hen = useRef<number | null>(null);
  // Máy chủ đã báo hết gửi. Cần NHỚ chứ không chỉ xử lý một lần: `done` thường
  // tới TRƯỚC khi mẩu cuối giải mã xong (giải mã là bất đồng bộ), nên lúc ấy
  // hàng chờ còn rỗng và không có gì để xả. Mẩu giải mã xong sau đó phải biết
  // là đừng chờ nữa — nếu không, một câu trả lời chỉ có một mẩu sẽ im đủ
  // `TRAN_CHO_MS` rồi mới cất tiếng.
  const hetGui = useRef(false);
  // ── SỐ THỨ TỰ MẨU CAO NHẤT ĐÃ NHẬN ───────────────────────────────────
  // Máy chủ đánh số mẩu tăng dần trong một lượt. Mẩu nào có số KHÔNG lớn hơn
  // số đã nhận là mẩu đã phát rồi.
  //
  // Có vì người dùng báo: nghe được một chút rồi khi chữ viết xong thì tiếng
  // đọc LẠI TỪ ĐẦU. Máy chủ chỉ gửi mỗi mẩu một lần, nên lượt đọc thứ hai sinh
  // ra ở phía này — `StrictMode` gắn lại thành phần làm dòng sự kiện vào hàng
  // đợi hai lần. Chặn bằng SỐ THỨ TỰ chứ không đi tìm nguồn phát: cái nào gửi
  // lại cũng bắt đầu lại từ 1.
  const daNhan = useRef(0);
  // Giải mã tuần tự. `decodeAudioData` bất đồng bộ, hai mẩu tới sát nhau có thể
  // giải mã xong NGƯỢC THỨ TỰ — đặt lịch theo thứ tự giải mã là đọc câu ba trước câu hai.
  const xep = useRef<Promise<void>>(Promise.resolve());

  const may = useCallback(() => {
    if (!ctx.current) {
      const C =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!C) return null;
      ctx.current = new C();
    }
    // Trình duyệt treo `AudioContext` khi trang chưa có tương tác nào. `resume()`
    // rẻ và im lặng khi đang chạy.
    //
    // NHƯNG không gỡ băng khi chính NGƯỜI DÙNG vừa bấm tạm dừng: hàm này chạy
    // ở mỗi mẩu tới, nên tạm dừng giữa lúc máy chủ còn đang gửi sẽ tự bung ra
    // sau vài trăm mili giây — nút bấm trông như hỏng.
    if (!tamRef.current) void ctx.current.resume().catch(() => {});
    return ctx.current;
  }, []);

  /** Đặt một mẩu vào lịch phát, nối đuôi mẩu trước. */
  const datLich = useCallback((c: AudioContext, buf: AudioBuffer, cau: number) => {
    const nut = c.createBufferSource();
    nut.buffer = buf;
    nut.connect(c.destination);
    // Tụt lại phía sau (mẩu tới chậm hơn tai nghe) thì bắt đầu ngay, đừng
    // đặt lịch vào một mốc đã trôi qua — `start()` với thời điểm quá khứ
    // phát ngay nhưng mốc kế tiếp sẽ sai và dồn lỗi về sau.
    const batDau = Math.max(c.currentTime + DEM_DAU, moc.current);
    nut.start(batDau);
    // ── KHOẢNG NGHỈ CHỈ Ở RANH GIỚI CÂU ────────────────────────────────────
    // `NGHI_GIUA_MAU` sinh ra cho chỗ nối giữa hai CÂU. Với dòng PCM, một câu
    // về thành nhiều mẩu 0,4 giây; chèn nghỉ giữa chúng là chèn vào giữa một
    // từ, và mỗi câu nhận thêm chục lần như thế.
    const noiCau = cauTruoc.current !== null && cauTruoc.current !== cau;
    cauTruoc.current = cau;
    moc.current = batDau + buf.duration + (noiCau ? NGHI_GIUA_MAU : 0);
    dangPhat.current.push(nut);
    setDangDoc(true);
    nut.onended = () => {
      dangPhat.current = dangPhat.current.filter((x) => x !== nut);
      if (!dangPhat.current.length) setDangDoc(false);
    };
  }, []);

  /** Kết thúc giai đoạn mồi: đặt lịch mọi mẩu đang chờ, theo đúng thứ tự. */
  const xa = useCallback(
    (c: AudioContext) => {
      if (hen.current !== null) {
        clearTimeout(hen.current);
        hen.current = null;
      }
      daChay.current = true;
      for (const m of cho.current) datLich(c, m.buf, m.cau);
      cho.current = [];
    },
    [datLich],
  );

  /**
   * Dòng SSE đã báo `done`. Máy chủ sẽ không gửi mẩu nào nữa, nên chờ thêm là
   * chờ một thứ không tồn tại — phát ngay những gì đang có.
   *
   * Đây là đường thoát cho câu trả lời chỉ có MỘT mẩu.
   */
  const xong = useCallback(() => {
    hetGui.current = true;
    const c = ctx.current;
    if (!c || daChay.current || !cho.current.length) return;
    xa(c);
  }, [xa]);

  /* Tạm dừng ↔ tiếp tục.
   *
   * ── VÌ SAO `suspend()` CHỨ KHÔNG `stop()` TỪNG NGUỒN ──────────────────
   * Mọi mẩu đã được ĐẶT LỊCH theo `ctx.currentTime`, và `suspend()` đóng băng
   * đúng cái đồng hồ ấy — nên lịch còn nguyên và `resume()` chạy tiếp đúng
   * chỗ. Dừng từng nguồn thì phải tự tính lại toàn bộ mốc cho phần chưa phát,
   * và mọi mẩu tới sau đó vẫn cứ thế phát ra.
   *
   * Cùng cơ chế `bamLoa` của `nhung.js` — hai nơi phát cùng một dòng byte thì
   * phải dừng theo cùng một luật.
   */
  const bamTam = useCallback(() => {
    const c = ctx.current;
    if (!c) return;
    if (c.state === 'running') {
      tamRef.current = true;
      void c.suspend().catch(() => {});
      setDangTam(true);
      return;
    }
    tamRef.current = false;
    void c.resume().catch(() => {});
    setDangTam(false);
  }, []);

  const dung = useCallback(() => {
    // ── PHẢI GỠ BĂNG TRƯỚC KHI LƯỢT SAU ĐẶT LỊCH ────────────────────────
    // `dung()` chạy ở đầu MỖI lượt hỏi. Người tạm dừng lượt trước rồi hỏi tiếp
    // sẽ để lại một `AudioContext` đang treo, và mọi mẩu của lượt mới đặt lịch
    // vào một cái đồng hồ đứng yên — im lặng tuyệt đối, không lỗi nào.
    tamRef.current = false;
    void ctx.current?.resume().catch(() => {});
    setDangTam(false);
    for (const n of dangPhat.current) {
      try {
        n.stop();
      } catch {
        // Đã dứt trước đó — `stop()` trên nút đã dừng thì ném. Đó chính là trạng thái mong muốn.
      }
    }
    dangPhat.current = [];
    moc.current = 0;
    daNhan.current = 0;
    cauTruoc.current = null;
    lichMau.current = new Float32Array(DUOI_LICH);
    viTri.current = 0;
    xep.current = Promise.resolve();
    cho.current = [];
    daChay.current = false;
    hetGui.current = false;
    if (hen.current !== null) {
      clearTimeout(hen.current);
      hen.current = null;
    }
    setDangDoc(false);
  }, []);

  /**
   * Nhận một mẩu tiếng từ sự kiện SSE `tieng`.
   *
   * HAI hình dạng, và bên gọi không phải biết mình đang nhận hình nào:
   *
   *   `wav`  — một tệp WAV hoàn chỉnh cho trọn một câu (đường `generateContent`)
   *   `pcm`  — PCM16 thô, một mảnh ~0,4 giây của câu số `cau` (đường Live)
   *
   * Hình thứ hai chỉ tới khi CHÍNH màn này đã khai `tiengDan: true` lúc hỏi,
   * nên không có ca "máy chủ mới gửi thứ bản cũ không đọc được".
   */
  const them = useCallback(
    (stt: number, goi: { wav?: string; pcm?: string; tanSo?: number; cau?: number }) => {
      if (stt <= daNhan.current) return;
      daNhan.current = stt;
      const c = may();
      if (!c) return;
      const cau = goi.cau ?? 0;

      /** Đặt lịch hoặc xếp vào hàng mồi — chung cho cả hai hình dạng. */
      const nhan = (buf: AudioBuffer) => {
        if (daChay.current) {
          datLich(c, buf, cau);
          return;
        }
        cho.current.push({ buf, cau });
        // Hết gửi rồi thì không còn gì để chờ, dù chưa đủ ngưỡng.
        const giay = cho.current.reduce((a, m) => a + m.buf.duration, 0);
        if (hetGui.current || giay >= NGUONG_GIAY_CHO) {
          xa(c);
          return;
        }
        // Hẹn giờ đặt ở mẩu ĐẦU TIÊN, không đặt lại ở mẩu sau: trần chờ tính
        // từ lúc có tiếng để phát, không phải từ lúc mẩu mới nhất tới.
        if (hen.current === null) hen.current = window.setTimeout(() => xa(c), TRAN_CHO_MS);
      };

      if (goi.pcm) {
        // Dựng thẳng, đồng bộ: không cần chuỗi `xep` vì không có gì bất đồng bộ
        // để chạy sai thứ tự.
        try {
          const kq = tuPcm16(
            c, giaiB64(goi.pcm), goi.tanSo ?? 24000, lichMau.current, viTri.current,
          );
          lichMau.current = kq.lich;
          viTri.current = kq.viTri;
          // `null` = mẩu ngắn hơn một bước nội suy, không có mẫu ra nào. Pha
          // đã mang sang rồi, nên bỏ qua mẩu này là đúng — đặt lịch một buffer
          // rỗng chỉ thêm một chỗ nối.
          if (kq.buf) nhan(kq.buf);
        } catch {
          // Một mẩu hỏng thì bỏ mẩu ấy, không treo cả hàng đợi.
        }
        return;
      }
      if (!goi.wav) return;
      const u = giaiB64(goi.wav);
      // Nối vào chuỗi: giữ ĐÚNG thứ tự tới, bất kể mẩu nào giải mã xong trước.
      xep.current = xep.current
        .then(() => c.decodeAudioData(u.buffer as ArrayBuffer))
        .then(nhan)
        .catch(() => {
          // Một mẩu hỏng thì bỏ mẩu ấy, không treo cả hàng đợi — và không đụng
          // `moc` để mẩu sau vẫn vào đúng nhịp.
        });
    },
    [may, datLich, xa],
  );

  // Rời màn hình thì im. Không có dòng này, âm thanh phát tiếp ở trang khác —
  // `AudioContext` thuộc về CỬA SỔ, không thuộc thành phần React nào.
  useEffect(
    () => () => {
      if (hen.current !== null) clearTimeout(hen.current);
      for (const n of dangPhat.current) {
        try {
          n.stop();
        } catch {
          /* đã dứt */
        }
      }
      void ctx.current?.close().catch(() => {});
      ctx.current = null;
    },
    [],
  );

  return { them, dung, xong, dangDoc, dangTam, bamTam };
}

// ── Nghe câu hỏi ─────────────────────────────────────────────────────────

interface BoNhan {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  onresult: ((e: SpeechResultEvent) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}

interface SpeechResultEvent {
  results: ArrayLike<ArrayLike<{ transcript: string }>>;
}

/** `SpeechRecognition` không có ở Firefox. Trả `null` để giao diện NÓI RA. */
function boNhan(): (new () => BoNhan) | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as Record<string, unknown>;
  return (w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null) as (new () => BoNhan) | null;
}

/**
 * Trình duyệt này có nhận dạng giọng nói không. Firefox không có `SpeechRecognition` — một nút bấm-không-phản-ứng tệ
 * hơn hẳn một nút vắng mặt, nên giao diện chỉ dựng mic khi có. Không có SSR nên đọc đồng bộ được.
 */
export function useCoNghe(): boolean {
  const [co] = useState(() => boNhan() !== null);
  return co;
}

export function useNghe(onXong: (van: string) => void) {
  const [dangNghe, setDangNghe] = useState(false);
  const may = useRef<BoNhan | null>(null);
  // `onXong` mới nhất, đọc được từ trong callback của bộ nhận. Bộ nhận sống lâu
  // hơn một lần dựng lại, nên đóng gói `onXong` vào nó là đóng gói bản của lần
  // dựng đầu — câu vừa nói sẽ đi vào một ô nhập đã cũ.
  const goi = useRef(onXong);
  useEffect(() => {
    goi.current = onXong;
  }, [onXong]);

  const batDau = useCallback(() => {
    const May = boNhan();
    if (!May) return;
    if (may.current) {
      may.current.abort();
      may.current = null;
      setDangNghe(false);
      return;
    }
    const m = new May();
    // `vi-VN` chứ không để mặc định: mặc định lấy ngôn ngữ trình duyệt, và một
    // máy cài tiếng Anh sẽ chép "thất thoát" thành "that twat".
    m.lang = 'vi-VN';
    m.interimResults = true;
    m.continuous = false;
    m.onresult = (e) => {
      let van = '';
      for (let i = 0; i < e.results.length; i++) {
        const ketQua = e.results[i];
        const dau = ketQua?.[0];
        if (dau) van += dau.transcript;
      }
      goi.current(van);
    };
    const het = () => {
      may.current = null;
      setDangNghe(false);
    };
    m.onend = het;
    m.onerror = het;
    may.current = m;
    setDangNghe(true);
    m.start();
  }, []);

  useEffect(() => () => may.current?.abort(), []);

  return { batDau, dangNghe };
}
